import { useQuery } from "@tanstack/react-query";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import api from "@/lib/api";
import { eur, num, showMoney } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Loader2 } from "lucide-react";

const Kpi = ({ label, value, sub, testid }) => (
  <Card className="p-4" data-testid={testid}>
    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className="font-display text-2xl font-semibold">{value}</p>
    {sub && <p className="text-[11px] text-muted-foreground">{sub}</p>}
  </Card>
);

const Delta = ({ a, b }) => {
  if (!b) return <span className="text-muted-foreground">—</span>;
  const d = Math.round(((a - b) / b) * 100);
  return <span className={d >= 0 ? "text-emerald-400" : "text-rose-400"}>{d >= 0 ? "+" : ""}{d}%</span>;
};

const Row = ({ c, m }) => (
  <tr className="border-t border-border/40" data-testid={`ads-campaign-${c.id}`}>
    <td className="max-w-[260px] py-2 pr-2"><p className="truncate font-medium">{c.name}</p><p className="text-[11px] text-muted-foreground">{c.start.split("-").reverse().join("/")} → {c.end.split("-").reverse().join("/")} · {c.active_days} gg</p></td>
    {m && <td className="text-right font-semibold">{eur(c.spend)}</td>}
    <td className="text-right">{num(c.reach)}</td>
    <td className="text-right">{num(c.link_clicks)}</td>
    <td className="text-right">{c.ctr.toFixed(2)}%</td>
    {m && <td className="text-right">{c.cpc == null ? "—" : eur(c.cpc)}</td>}
    <td className="text-right">{c.purchases ? `${num(c.purchases)}${m ? ` · ${eur(c.purchase_value)}` : ""}` : "—"}</td>
    {m && <td className="text-right">{c.roas_meta == null || !c.purchase_value ? "—" : `${c.roas_meta.toFixed(1)}x`}</td>}
    {m && <td className="text-right">{eur(c.shopify_during)}</td>}
    {m && <td className="text-right text-xs"><Delta a={c.shopify_during} b={c.shopify_before} /></td>}
  </tr>
);

export const SocialAds = ({ days }) => {
  const d = Math.max(days, 30);
  const { data } = useQuery({ queryKey: ["social", "ads", d], queryFn: async () => (await api.get("/social/ads", { params: { days: d } })).data });
  if (!data) return <Loader2 className="h-6 w-6 animate-spin text-primary" />;
  const t = data.totals;
  const m = showMoney();
  return (
    <div className="space-y-6" data-testid="social-ads">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {m && <>
        <Kpi label={`Spesa sponsorizzate · ${d} gg`} value={eur(t.spend)} testid="ads-kpi-spend" />
        <Kpi label="Vendite Shopify stesso periodo" value={eur(t.sales)} sub={t.spend ? `${(t.sales / t.spend).toFixed(1)} € venduti per ogni € speso` : null} testid="ads-kpi-sales" />
        <Kpi label="Acquisti attribuiti da Meta" value={eur(t.purchase_value)} sub={t.spend && t.purchase_value ? `ROAS Meta ${(t.purchase_value / t.spend).toFixed(1)}x` : "richiede il Pixel/API conversioni"} testid="ads-kpi-meta" />
        </>}
        <Kpi label="Clic sul link" value={num(t.link_clicks)} sub={m && t.link_clicks ? `${eur(t.spend / t.link_clicks)} a clic` : null} testid="ads-kpi-clicks" />
      </div>
      {m && <Card className="p-5">
        <p className="mb-3 font-mono-erp text-[11px] uppercase tracking-widest text-muted-foreground">Spesa giornaliera (barre) e vendite Shopify (linea)</p>
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={data.series}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
            <XAxis dataKey="date" tickFormatter={(x) => x.slice(8, 10) + "/" + x.slice(5, 7)} fontSize={11} />
            <YAxis yAxisId="s" fontSize={11} width={50} />
            <YAxis yAxisId="v" orientation="right" fontSize={11} width={60} />
            <Tooltip formatter={(v, n) => [eur(v), n === "spend" ? "Spesa" : "Vendite Shopify"]} />
            <Legend formatter={(n) => (n === "spend" ? "Spesa" : "Vendite Shopify")} />
            <Bar yAxisId="s" dataKey="spend" fill="hsl(var(--primary))" radius={[2, 2, 0, 0]} />
            <Line yAxisId="v" dataKey="sales" stroke="hsl(var(--muted-foreground))" dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </Card>}
      <Card className="overflow-x-auto p-5" data-testid="ads-campaigns">
        <table className="w-full min-w-[980px] text-sm">
          <thead><tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
            <th className="pb-2">Campagna</th>{m && <th className="pb-2 text-right">Spesa</th>}<th className="pb-2 text-right">Copertura</th><th className="pb-2 text-right">Clic link</th>
            <th className="pb-2 text-right">CTR</th>{m && <th className="pb-2 text-right">Costo clic</th>}<th className="pb-2 text-right">Acquisti Meta</th>{m && <th className="pb-2 text-right">ROAS</th>}
            {m && <><th className="pb-2 text-right">Vendite Shopify nel periodo</th><th className="pb-2 text-right">vs periodo prima</th></>}
          </tr></thead>
          <tbody>{data.campaigns.map((c) => <Row key={c.id} c={c} m={m} />)}</tbody>
        </table>
        {!data.campaigns.length && <p className="py-4 text-sm text-muted-foreground" data-testid="ads-empty">Nessuna campagna con spesa in questo periodo (o dati ancora in arrivo)</p>}
        {m && <p className="mt-2 text-[11px] text-muted-foreground">"Vendite Shopify nel periodo" = tutto il fatturato dei giorni in cui la campagna era attiva, confrontato con lo stesso numero di giorni subito prima: indica l'effetto complessivo, non solo gli ordini arrivati dall'inserzione.</p>}
      </Card>
    </div>
  );
};
